// PO_ATTACHMENT_FLOW_V1: no retries and no optimistic success for financial writes.
export function positivePoId(value) {
  const raw = String(value ?? '');
  if (!/^[1-9]\d*$/.test(raw) || !Number.isSafeInteger(Number(raw))) {
    throw new Error('The server did not return a valid PO/document reference.');
  }
  return Number(raw);
}
export function outcomeUncertain(error) {
  const status = error?.response?.status;
  return !Number.isInteger(status) || status === 408 || status >= 500;
}
export class PoAttachmentFlowError extends Error {
  constructor(cause, phase, poId, uncertain) {
    super(cause?.message || 'PO operation could not be completed.');
    this.name = 'PoAttachmentFlowError';
    this.cause = cause;
    this.phase = phase;
    this.poId = poId;
    this.uncertain = uncertain;
    this.reviewRequired = cause?.reviewRequired === true;
  }
}
function checkedPo(data, expectedId, expectedStatus) {
  const id = positivePoId(data?.po_id);
  if ((expectedId && id !== positivePoId(expectedId)) || data?.status !== expectedStatus) {
    throw new Error('The PO save response could not be confirmed. Check the saved order before retrying.');
  }
  return { ...data, po_id: id };
}

// Transport and callbacks are injected so failure behaviour can be tested without a live API.
export async function runPoAttachmentFlow({
  intent, draftId, payload, queue = [], transport,
  onDraftSaved = () => {}, onFileState = () => {}, onPhase = () => {}, onFinancialVerified = () => {},
  isCurrent = () => true,
}) {
  let poId = draftId ? positivePoId(draftId) : null;
  let phase = 'validation';
  let writing = false;
  let financials = null;
  const verify = async status => {
    financials = await transport.verifyFinancials(poId, payload, status);
    if (financials) onFinancialVerified(financials);
  };
  const current = () => {
    if (!isCurrent()) throw new Error('The page or account changed. Reopen the saved draft before continuing.');
  };
  const enter = (next, write = false) => { current(); phase = next; writing = write; onPhase(next); };
  try {
    current();
    if (!['draft', 'submit'].includes(intent)) throw new Error('Invalid PO action.');
    if (queue.some(file => file.state === 'uncertain' || file.state === 'uploading')) {
      throw new Error('Resolve the unconfirmed upload before saving or submitting.');
    }
    if (queue.length && !payload.cost_centre_id) {
      throw new Error('Select a cost centre before saving supporting documents.');
    }
    // Preserve direct creation for the existing no-attachments workflow.
    if (intent === 'submit' && !poId && queue.length === 0) {
      enter('create-submitted', true);
      const po = checkedPo(await transport.create({ ...payload, status: 'Pending', save_as_draft: false, is_draft: false }), null, 'Pending');
      poId = po.po_id;
      if (transport.verifyFinancials) {
        onDraftSaved(po);
        enter('verify-financials');
        await verify('Pending');
      }
      return { po, submitted: true, documents: null };
    }
    const draftPayload = { ...payload, status: 'Draft', save_as_draft: true, is_draft: true };
    enter(poId ? 'save-draft' : 'create-draft', true);
    const draft = checkedPo(poId ? await transport.update(poId, draftPayload) : await transport.create(draftPayload), poId, 'Draft');
    poId = draft.po_id;
    onDraftSaved(draft); // Record ID BEFORE attempting any attachment request.
    if (transport.verifyFinancials) {
      enter('verify-financials');
      await verify('Draft');
    }
    const confirmedIds = [];
    for (const entry of queue) {
      current();
      if (entry.state === 'saved') {
        confirmedIds.push(positivePoId(entry.documentId));
        continue;
      }
      enter('upload', true);
      onFileState(entry.key, { state: 'uploading', progress: 0 });
      try {
        const document = await transport.upload(poId, entry, progress => onFileState(entry.key, { progress }));
        const documentId = positivePoId(document?.document_id);
        const linked = document?.links?.some(link => link.entity_type === 'purchase_order' && String(link.entity_id) === String(poId) && link.is_active === true);
        if (!linked) throw new Error('The upload response did not confirm its PO link.');
        confirmedIds.push(documentId);
        onFileState(entry.key, { state: 'saved', progress: 100, documentId });
      } catch (error) {
        onFileState(entry.key, { state: outcomeUncertain(error) ? 'uncertain' : 'failed', message: 'Upload was not confirmed.' });
        throw error;
      }
    }
    let documents = null;
    if (queue.length) {
      enter('verify-documents');
      documents = await transport.list(poId);
      if (!Array.isArray(documents) || confirmedIds.some(id => !documents.some(row => String(row.document_id) === String(id) && row.entity_type === 'purchase_order' && String(row.entity_id) === String(poId)))) {
        throw new Error('A saved attachment was not confirmed in the PO document list. Submission was not sent.');
      }
    }
    current();
    if (intent === 'draft') return { po: draft, submitted: false, documents };
    enter('submit', true);
    const version = financials?.header?.fx_snapshot_version;
    const submitPayload = version ? {...payload,expected_fx_version:version} : payload;
    const po = checkedPo(await transport.submit(poId, submitPayload), poId, 'Pending');
    if (transport.verifyFinancials) {
      onDraftSaved(po);
      enter('verify-financials');
      await verify('Pending');
    }
    return { po, submitted: true, documents };
  } catch (error) {
    throw new PoAttachmentFlowError(error, phase, poId, writing && outcomeUncertain(error));
  }
}
